import { msg } from "@lingui/core/macro";
import { Button } from "@/components/ui/button";
import { TextField } from "@/components/ui/text-field";
import { AnimatedSection } from "@/components/ui/animated-section";
import { Plus } from "lucide-solid";
import { useI18n } from "@/providers/i18n-provider";

interface TodoListAddBarProps {
  /** When false the bar is hidden via AnimatedSection */
  show: boolean;
  value: string;
  isSubmitting: boolean;
  onInput: (value: string) => void;
  onSubmit: (e: Event) => void;
  /** Ref callback so the parent can focus the input programmatically */
  inputRef?: (el: HTMLInputElement) => void;
}

export function TodoListAddBar(props: TodoListAddBarProps) {
  const { t } = useI18n();

  return (
    <div class="shrink-0 border-t border-border/40 bg-background/95 backdrop-blur-sm px-6 pb-[calc(env(safe-area-inset-bottom,0px))] z-20">
      <AnimatedSection show={props.show}>
        <div class="py-2.5">
          <form
            onSubmit={(e) => {
              props.onSubmit(e);
            }}
            class="flex gap-2"
          >
            <div class="relative flex-1 group/input">
              <TextField
                ref={props.inputRef}
                placeholder={t(msg`Add a task...`)}
                value={props.value}
                onInput={(e) => {
                  props.onInput(e.currentTarget.value);
                }}
                onKeyDown={(e) => {
                  if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                    props.onSubmit(e);
                  }
                }}
                class="w-full pr-10"
                disabled={props.isSubmitting}
              />
              {/* Keyboard shortcut hint — fades out when input is focused */}
              <div
                class="absolute right-3 top-1/2 -translate-y-1/2 flex items-center select-none cursor-help group-focus-within/input:opacity-0 transition-opacity duration-150"
                title={t(msg`Quick focus: Press N to focus the add task input field`)}
              >
                <kbd class="h-4.5 min-w-[16px] inline-flex items-center justify-center rounded border border-border/60 bg-background px-1.5 font-mono text-[9px] font-bold text-foreground/80 shadow-sm">
                  N
                </kbd>
              </div>
            </div>

            <Button type="submit" disabled={!props.value.trim() || props.isSubmitting}>
              <Plus class="mr-2 h-4 w-4" />
              {t(msg`Add`)}
            </Button>
          </form>
        </div>
      </AnimatedSection>
    </div>
  );
}
